<template>
  <div class="quick-add">
    <button
      v-if="product"
      class="quick-add__add-to-cart"
      :class="{
        [$class('success')]: state === 'added',
        [$class('error')]: state === 'error',
      }"
      :disabled="!isAvailable || (disableCta && state !== 'adding')"
      type="button"
      @click.prevent.stop="checkAddToCart"
    >
      <span
        class="visually-hidden"
        v-text="$string('product.cta.add_to_cart')"
      />

      <component :is="addToCartIcon" />
    </button>
    <teleport to="body">
      <overlay
        v-if="
          (useProductState || variantsShowQatcOverlay) &&
          product &&
          selectedVariant
        "
        :ref="`quickAddModal-${product.handle}`"
        block-class="quick-add-select-options"
        direction="up"
        :namespace="`quickAddModal-${product.handle}-${variant.id}`"
        type="shelf-modal"
      >
        <template #body="overlay">
          <a class="quick-add-select-options__content" :href="product.url">
            <div class="quick-add-select-options__image-container">
              <responsive-image
                class="quick-add-select-options__image"
                :image="featuredImage"
                ratio="1:1"
                sizes="(min-width: 1024px) 178px, 120px"
                widths="178, 356, 534"
              />
            </div>

            <div class="quick-add-select-options__title">
              <p
                class="product-card-select__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
                v-text="product.title"
              />

              <product-prices
                class="product-card-select__prices"
                :compare-at-price="selectedVariant?.compareAtPrice?.amount"
                :discount-logic="getDiscountLogic(selectedVariant)"
                :price="selectedVariant?.price?.amount"
                template="card"
                :unit-price="selectedVariant?.unitPrice?.amount"
                :unit-price-measurement="selectedVariant?.unitPriceMeasurement"
                :is-gift-card="product.giftCard"
              />
            </div>
          </a>

          <form
            class="quick-add-select-options__form"
            @submit.prevent="handleUpdateSelectedVariant"
          >
            <product-options
              v-if="overlay.isActive"
              :key="`quickAddModal-${product.handle}`"
              class="product-form__options"
              :loading="loading"
              :product="product"
              :section="{
                id: product.id,
                optionStylingTile: [],
                optionStylingImageTile: [],
              }"
              :variant="selectedVariant"
              :variant-from-url="false"
              @update-variant="handleUpdateOption"
            />

            <notify-me
              v-if="showNotify"
              ref="notifyMe"
              class="quick-add-select-options__notify-me"
              :variant-id="selectedVariant.id"
              :product-id="product.id"
              @update-success="handleNotifyMeSuccess"
            />

            <div
              v-if="showCta"
              class="quick-add-select-options__button-wrapper"
            >
              <btn
                class="quick-add-select-options__save"
                :label="addToCartText"
                modifiers="secondary cta center"
                :show-state-icon="state === 'add_notify'"
                :show-icon="layerAddToCartIcon !== ''"
                type="submit"
                :disabled="
                  !selectedVariant?.available && !showBackInStockEmailForm
                "
                @click="handleAddToCart"
              >
                <template #icon>
                  <component :is="layerAddToCartIcon" />
                </template>

                <template #stateIcon>
                  <icon-loading />
                </template>
              </btn>
            </div>
          </form>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: quick-add (quick-add)
 * -----------------------------------------------------------------------------
 * Quick add to card button for product cards.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
import { mapActions, mapState } from 'pinia';
import useToastNotificationsStore from '~stores/toast-notifications';
import useOverlaysStore from '~stores/overlays';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import Btn from '~global/btn/btn';
import IconAdd from '~icons/general/add.svg';
import IconCheck from '~icons/general/check.svg';
import IconCross from '~icons/general/cross.svg';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import IconEmail from '~icons/general/email.svg';
import IconLoading from '~icons/general/loading.svg';
import NotifyMe from '~async/product-form/notify-me';
import Overlay from '~global/overlay/overlay';
import ProductOptions from '~async/product-form/product-options';
import ProductBadges from '~global/product-badges/product-badges';
import ProductPrices from '~global/product-prices/product-prices';
import ResponsiveImage from '~global/images/responsive-image';

import { formatTrackingItem } from '~/format/format-tracking-item';
import { computeDiscountLogic } from '~/helpers/vue';

export default {
  name: 'QuickAdd',

  components: {
    Btn,
    IconAdd,
    IconCheck,
    IconChevronDown,
    IconCross,
    IconEmail,
    IconLoading,
    NotifyMe,
    Overlay,
    ProductOptions,
    ProductBadges,
    ProductPrices,
    ResponsiveImage,
  },

  inject: {
    variantsShowQatcOverlay: {
      from: 'variantsShowQatcOverlay',
      default: true,
    },
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    useProductState: {
      type: Boolean,
      default: false,
    },
    campaignCard: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'track-click': () => true,
  },

  data() {
    return {
      state: 'ready',
      selected: true,
      selectedVariant: {},
      showCta: true,
      disableCta: false,
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    ...mapState(useCartStore, {
      cartStatus: 'status',
      depositItemUpdate: 'depositItemUpdate',
    }),

    isAvailable() {
      const state = this.useProductState ? this.product : this.variant;

      // If the product is a campaign card, we check the variants for availability.
      if (this.campaignCard) {
        return this.product?.variants?.filter(
          (variant) =>
            variant.available && variant.availableState === 'available',
        ).length;
      }

      return !(
        state.availableState === 'back-soon' ||
        state.availableState === 'sold-out' ||
        state.availableState === 'hidden'
      );
    },

    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      switch (this.state) {
        case 'adding':
          return 'icon-loading';
        case 'added':
          return 'icon-check';
        case 'error':
          return 'icon-cross';
        default:
          return 'icon-add';
      }
    },

    /**
     * Compute cta button icon.
     * @returns {String}
     */
    layerAddToCartIcon() {
      if (this.showNotify) {
        return 'icon-email';
      }
      return '';
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.selectedVariant) {
        return this.$string('product.cta.select_variant');
      }

      if (this.variant && !this.selectedVariant.available) {
        if (this.showBackInStockEmailForm) {
          return this.$string('product.cta.notify_me');
        }
        return this.$string('product.cta.sold_out');
      }

      return this.$string('product.cta.add_to_cart');
    },

    /**
     * Checks whether to use variant first image.
     * - Use variant featured image (grey background) in product grid.
     * @returns {Object}
     */
    featuredImage() {
      if (this.selectedVariant) {
        if (
          this.selectedVariant.media &&
          this.selectedVariant.media.length > 0
        ) {
          return this.selectedVariant.media[0].image;
        }

        if (
          this.selectedVariant.featuredImage &&
          Object.keys(this.selectedVariant.featuredImage).length > 0
        ) {
          return this.selectedVariant.featuredImage;
        }
      }

      if (this.product.media && this.product.media.length > 0) {
        return this.product.media[0].image;
      }

      return this.product.featuredImage;
    },

    /**
     * Checks configuration for back in stock email form.
     * @returns {boolean}
     */
    showBackInStockEmailForm() {
      return this.selectedVariant?.showBackInStockEmailForm;
    },

    /**
     * Checks if the current variant is available.
     * @returns {boolean} True if the variant exists and is available.
     */
    isVariantAvailable() {
      return this.selectedVariant?.available;
    },

    /**
     * Checks if the current variant's state is either 'back-soon'
     * or 'sold-out'.
     * @returns {boolean} True if the variant's availableState is 'back-soon'
     * or 'sold-out'.
     */
    isVariantBackSoonOrSoldOut() {
      return ['back-soon', 'sold-out'].includes(
        this.selectedVariant.availableState,
      );
    },

    hasOnlyOneVariant() {
      return (
        this.product?.variants?.filter(
          (variant) =>
            variant.available && variant.availableState === 'available',
        ).length === 1
      );
    },

    /**
     * Checks if notify me form should be shown instead of add to cart.
     * @returns {boolean} True if the variant exists and is available.
     */
    showNotify() {
      return (
        this.showBackInStockEmailForm &&
        (!this.isVariantAvailable || this.isVariantBackSoonOrSoldOut)
      );
    },
  },

  watch: {
    /**
     * Watch current variant to update form's selected variant.
     */
    selectedVariant() {
      this.soldOut = Boolean(!this.selectedVariant?.available);

      this.showCta =
        !this.$refs.notifyMe ||
        (this.$refs.notifyMe &&
          !this.$refs.notifyMe.submittedVariantIds.includes(
            this.selectedVariant.id,
          ));
    },

    /**
     * Watch product to update current variant.
     */
    product() {
      this.selectedVariant = this.variant
        ? this.variant
        : this.product?.selectedOrFirstAvailableVariant;
    },
    /**
     * Watch variant to update current variant.
     */
    variant() {
      this.selectedVariant = this.variant;
    },

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      this.disableCta =
        this.selectedVariant?.id &&
        this.cartStatus.items.includes(this.selectedVariant.id);
    },
  },

  mounted() {
    this.selectedVariant = this.variant
      ? this.variant
      : this.product?.selectedOrFirstAvailableVariant;

    this.soldOut = Boolean(!this.selectedVariant?.available);
  },

  methods: {
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
    }),
    ...mapActions(useToastNotificationsStore, ['addToast']),
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
      closeOverlay: 'close',
    }),

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateOption(variant) {
      this.selectedVariant = variant;
    },

    /**
     * Get discount logic.
     * @param {Boolean|Object} variant - Variant object to use.
     * @returns {Object}
     */
    getDiscountLogic(variant) {
      return computeDiscountLogic({
        discountPreview: false,
        onSale: variant?.compareAtPriceDiscount,
        product: this.product,
        variant,
      });
    },

    /**
     * Checks on first button click if product should go directly to cart.
     * Or if we have to show variant selection layer.
     */
    async checkAddToCart() {
      if (this.state === 'adding') {
        return;
      }
      this.$emit('track-click');

      if (
        this.product.hasOnlyDefaultVariant ||
        this.hasOnlyOneVariant ||
        (!this.useProductState && !this.variantsShowQatcOverlay)
      ) {
        await this.handleAddToCart();
      } else {
        this.openOverlay({
          ignoreDismissed: true,
          namespace: `quickAddModal-${this.product.handle}-${this.variant.id}`,
        });
      }
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      let depositVariantId;
      if (!this.selectedVariant) {
        return;
      }

      /**
       * Notify me.
       * - Uses child component method.
       */
      if (!this.selectedVariant.available) {
        try {
          this.state = 'add_notify';
          await this.$refs.notifyMe.handleNotifyMe();
          this.state = 'ready';
        } catch (error) {
          this.state = 'error';
          cnvs.ReportError('Failed to submit notify me', error);
        }

        return;
      }

      const items = [
        {
          id: this.selectedVariant.id,
          quantity: 1,
        },
      ];

      if (
        this.selectedVariant.canDeposit === 'true' &&
        this.$variable('store.country') === 'DE'
      ) {
        depositVariantId = this.selectedVariant.id;
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          depositVariantId,
        ]);
        const depositProduct = {
          id: this.$variable('product.depositProductID'),
          quantity: this.selectedVariant.canDepositAmount,
        };
        items.push(depositProduct);
      }

      if (!this.product.hasOnlyDefaultVariant) {
        this.closeOverlay(
          `quickAddModal-${this.product.handle}-${this.variant.id}`,
        );
      }

      /**
       * Add to cart.
       */
      try {
        this.state = 'adding';
        await this.addItem(items);
        this.state = 'added';
        this.showToastSuccess();

        // Tracking event add qty to item in cart
        this.handleTrackingAddItem();

        await this.resetState();
      } catch (error) {
        this.state = 'error';

        this.showToastError(error.description);

        await this.resetState();

        if (error.description) {
          return;
        }

        cnvs.ReportError('Failed to add product sample to cart', error);
      }

      this.setDepositItemUpdate(
        this.depositItemUpdate.filter((id) => id !== depositVariantId),
      );
    },

    /**
     * Handle update on the notify me component - if requests has success.
     * @param {Boolean} success - Wether the request was successful or not.
     */
    handleNotifyMeSuccess(success) {
      if (success) {
        this.showCta = false;
      } else {
        this.state = 'error';
      }
    },

    /**
     * If add was successful, show success toast.
     */
    showToastSuccess() {
      this.addToast({
        title: this.$string('toast.quick_add_success'),
        id: 'quick-add-success',
        ctaText: this.$string('toast.open_cart'),
        type: 'success',
        callback: () => {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
        },
      });
    },

    /**
     * if somethign went wrong, show error toast with response message.
     * @param {String} errorMessage - Error description
     */
    showToastError(errorMessage) {
      this.addToast({
        title: errorMessage,
        id: 'quick-add-error',
        ctaText: this.$string('toast.open_cart'),
        type: 'error',
        callback: () => {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
        },
      });
    },

    /**
     * Tracking event add qty to item in cart.
     */
    handleTrackingAddItem() {
      const percentageDiscount = this.getCampaignDiscount(
        this.discountLogic,
        'active',
      );
      const discountObject = {
        percentageDiscount,
        discountCode: this.discountCode,
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'Quick Add To Cart',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: this.quantity,
      });

      cnvs.EventBus.emit('dataLayer:add_to_cart', {
        event: 'add_to_cart',
        currency: cnvs.store.currency,
        location: 'Quick Add To Cart',
        items: [item],
      });
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'));
      });

      this.state = 'ready';
    },
  },
};
</script>

<style lang="scss">
@import './quick-add';
</style>
